import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import loadable from "@loadable/component";
import getWindow from "../utils/window";
import ErrorBoundary from "../components/errorBoundary/ErrorBoundary";
import PriceData from "../components/priceData/PriceData";
import ProductName from "../components/productName/ProductName";
import ClientOnly from "../containers/clientOnly/ClientOnly";
import OutOfStockLabel from "../components/outOfStockLabels/OutOfStockLabel";
import { ProductContextProvider } from "../context/product/ProductContext";
import { VariantContextProvider } from "../context/variant/VariantContext";
import { MySizeProfileContextProvider } from "../context/mySizeProfile/MySizeProfileContext";
import MySizeProfileModal from "../components/mySizeProfile/MySizeProfileModal";
import ProductColourContainer from "../components/productColourContainer/ProductColourContainer";
import ProductPromo from "../components/productPromo/ProductPromo";
import ReleaseDateBanner from "../components/releaseDateBanner/ReleaseDateBanner";
import EarlyAccessToSaleBox from "../components/earlyAccessToSaleBox/EarlyAccessToSaleBox";
import PriceOnApp from "../components/priceComponents/priceOnApp/PriceOnApp";
import MembersDayBox from "../components/membersDayBox/MembersDayBox";
import ProductSize from "../components/productSize/ProductSize";
import Rating from "../components/ratingComponents/rating/Rating";
import SubRatings from "../components/subRatings/v2/SubRatingsv2";
import { PRODUCT_COLOUR_POSITIONS } from "../constants";
import ProductCode from "../components/productCode/ProductCode";
import CustomerFitRating from "../components/customerFitRating/CustomerFitRating";
import VariantSelectionContainer from "../containers/variantSelection/VariantSelectionContainer";
import { SizingContextProvider } from "../context/sizing/SizingContext";
import DeliveryAndReturnsContainer from "../containers/deliveryAndReturnsContainer/DeliveryAndReturnsContainer";
import IfInStock from "../components/conditional/IfInStock";
import StockPriceRetry from "../components/stockPriceRetry/StockPriceRetry";
import LiveRegion from "../components/liveRegion/LiveRegion";
import ProductDescription from "../components/productDescription/ProductDescription";
import IngredientsContainer from "../containers/IngredientsContainer/IngredientsContainer";
import SkeletonPrimaryActions from "../components/skeletonPrimaryActions/SkeletonPrimaryActions";
import { useIsInStockContext } from "../context/isInStock/IsInStockContext";
import useSingleProductData from "../hooks/useSingleProductData";
import { useRuntimeConstants } from "../context/runtimeConstants/RuntimeConstantsContext";
import IfStockStateReady from "../components/conditional/IfStockStateReady";
import ShowOnProductStockStatus from "../components/conditional/ShowOnProductStockStatus";
import {
  fireAddToBagAnalytics,
  fireAddToBagErrorAnalytics,
} from "../analytics/addToBag";
import { fireAddToBagMarketing } from "../utils/marketingTracking/marketingTrackingEvents";
import fireNotifyMeClickAnalytics from "../analytics/backInStock";
import { fireSourcingLocationAnalytics } from "../analytics/sourcingLocation";
import Spacing, { SPACING } from "../components/spacing/Spacing";
import RestockingSoonBanner from "../components/restockingSoonBanner/RestockingSoonBanner";
import ModelWearsPortal from "../components/modelWears/ModelWearsPortal";
import styles from "./SingleProductPage.scss";

const OutOfStockHero = loadable(
  () => import(/* webpackChunkName: "deferred" */ "../LoadableComponents"),
  {
    ssr: false,
    resolveComponent: ({ OutOfStockHero }) => OutOfStockHero,
  }
);

const App = loadable(
  () => import(/* webpackChunkName: "deferred" */ "../LoadableComponents"),
  {
    ssr: false,
    resolveComponent: ({ App }) => App,
  }
);

const SocialShare = loadable(
  () => import(/* webpackChunkName: "deferred" */ "../LoadableComponents"),
  {
    ssr: false,
    resolveComponent: ({ SocialShare }) => SocialShare,
  }
);

const RelatedCategories = loadable(
  () => import(/* webpackChunkName: "deferred" */ "../LoadableComponents"),
  {
    ssr: false,
    resolveComponent: ({ RelatedCategories }) => RelatedCategories,
  }
);

const RelatedCategoriesElement = () => {
  const { relatedCategories } = useRuntimeConstants();
  const win = getWindow();
  if (!win || !relatedCategories) return null;

  const container = win.document.getElementById("related-categories");
  if (!container) return null;

  if (!container.dataset.rcCleared) {
    container.textContent = "";
    container.dataset.rcCleared = "true";
  }

  return ReactDOM.createPortal(<RelatedCategories />, container);
};

export default function SingleProductPage({ ratings = { subRatings: [] } }) {
  const { product, mainProducts, labeledSubRatings, showSizingScale } =
    useSingleProductData(ratings);
  const { isInStock } = useIsInStockContext();

  return (
    <>
      <ProductContextProvider products={mainProducts} productId={product.id}>
        <MySizeProfileContextProvider>
          <VariantContextProvider
            fireSourcingLocationAnalytics={fireSourcingLocationAnalytics}
          >
            {getWindow()
              ? ReactDOM.createPortal(
                  <OutOfStockHero />,
                  getWindow().document.getElementById("pdp-oos-hero")
                )
              : null}
            <ModelWearsPortal />
            <RelatedCategoriesElement />
            <LiveRegion />
            <ClientOnly>
              <ProductColourContainer position={PRODUCT_COLOUR_POSITIONS.TOP} />
            </ClientOnly>
            <div className={styles.titleContainer}>
              <Spacing size={SPACING.TWELVE} excludeFirst={true}>
                <ShowOnProductStockStatus productStockStatus={false}>
                  <OutOfStockLabel />
                </ShowOnProductStockStatus>
                <ProductName />
              </Spacing>
              <SocialShare />
            </div>
            <Spacing size={SPACING.TWELVE}>
              <StockPriceRetry />
              <PriceData />
              <PriceOnApp />
              <SkeletonPrimaryActions />
              {!showSizingScale && <Rating isInStock={isInStock} />}
            </Spacing>
            <Spacing size={SPACING.SIXTEEN}>
              <RestockingSoonBanner />
            </Spacing>
            <Spacing size={SPACING.TWENTY}>
              <ProductPromo />
              <MembersDayBox />
              <ClientOnly>
                <ProductColourContainer
                  position={PRODUCT_COLOUR_POSITIONS.MIDDLE}
                />
                {showSizingScale && (
                  <>
                    <Rating isInStock={isInStock} />
                    <SubRatings
                      additionalClasses={styles.mainSubRatings}
                      subRatings={labeledSubRatings}
                    />
                  </>
                )}
                <IfInStock>
                  <CustomerFitRating />
                </IfInStock>
              </ClientOnly>
              <ReleaseDateBanner />
              <EarlyAccessToSaleBox />
              <SizingContextProvider>
                <IfInStock>
                  <ProductSize />
                </IfInStock>
                <IfStockStateReady>
                  <VariantSelectionContainer
                    isInStock={isInStock}
                    shouldShowSave={true}
                    fireAddToBagAnalytics={fireAddToBagAnalytics}
                    fireAddToBagErrorAnalytics={fireAddToBagErrorAnalytics}
                    fireAddToBagMarketing={fireAddToBagMarketing}
                    fireNotifyMeClickAnalytics={fireNotifyMeClickAnalytics}
                    isLooksProduct={false}
                  />
                </IfStockStateReady>
                <IfInStock>
                  <DeliveryAndReturnsContainer />
                </IfInStock>
                <ProductDescription />
              </SizingContextProvider>
            </Spacing>
            <IngredientsContainer />
            <ProductCode />
          </VariantContextProvider>
          <IfInStock>
            <MySizeProfileModal />
          </IfInStock>
        </MySizeProfileContextProvider>
      </ProductContextProvider>

      {getWindow()
        ? ReactDOM.createPortal(
            <ErrorBoundary>
              <App />
            </ErrorBoundary>,
            getWindow().document.getElementById("pdp-react-app")
          )
        : null}
    </>
  );
}

SingleProductPage.propTypes = {
  ratings: PropTypes.shape({
    subRatings: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        minLabel: PropTypes.string.isRequired,
        midLabel: PropTypes.string.isRequired,
        maxLabel: PropTypes.string.isRequired,
        percentage: PropTypes.number.isRequired,
      }).isRequired
    ).isRequired,
  }),
};
